import { defineStore } from 'pinia';
import { useConnectionStore } from '@/stores/connection';
import type { Mutation, TeamRoleInput } from '@/generated/graphql';
import type * as TeamRoles from '@/types/TeamRole';
import * as TeamRoleDetails from '@/types/TeamRoleDetails';
import type RequestResult from '@/types/RequestResult';
import * as apolloCore from '@apollo/client/core';

const { gql } = apolloCore;

const GET_ALL_TEAM_ROLES_QUERY = gql`
  query getAllTeamRoles($accountId: Int!) {
    app {
      teamRoles(accountId: $accountId) {
        id
        name
      }
    }
  }
`;

const TEAM_ROLE_DETAILS_FRAGMENT = gql`
  fragment TeamRoleDetailsFields on TeamRoleDetails {
    id
    name
    permissionGroups {
      icon
      key
      label
      permissions {
        icon
        key
        label
        level
        options {
          label
          value
        }
      }
    }
    allowances {
      enabled
      key
      label
    }
  }
`;
const GET_TEAM_ROLE_DETAILS_QUERY = gql`
  query getTeamRoleDetails($roleId: Int!) {
    app {
      teamRoleDetails(roleId: $roleId) {
        ...TeamRoleDetailsFields
      }
    }
  }
  ${TEAM_ROLE_DETAILS_FRAGMENT}
`;

const SAVE_TEAM_ROLE_MUTATION = gql`
  mutation saveTeamRoleMutation($input: TeamRoleInput!) {
    app {
      saveTeamRole(input: { teamRole: $input }) {
        clientMutationId
        errors
        teamRoleDetails {
          ...TeamRoleDetailsFields
        }
      }
    }
  }
  ${TEAM_ROLE_DETAILS_FRAGMENT}
`;

const DELETE_TEAM_ROLE_MUTATION = gql`
  mutation deleteTeamRoleMutation($teamRoleId: Int!) {
    app {
      deleteTeamRole(input: { teamRoleId: $teamRoleId }) {
        clientMutationId
        errors
      }
    }
  }
`;
interface TeamRoleState {
  teamRoles: TeamRoles.TeamRole[];
  teamRoleDetails: TeamRoleDetails.TeamRoleDetails | null;
  loading: boolean;
}

export const useTeamRolesStore = defineStore('teamRoles', {
  state: (): TeamRoleState => ({
    teamRoles: [],
    // TODO: After sorting the type above, this should probably an empty object
    teamRoleDetails: null,
    loading: false,
  }),

  actions: {
    async fetchAllTeamRoles(accountId: number) {
      this.loading = true;
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      try {
        const { data, errors } = await apiClient.query({
          query: GET_ALL_TEAM_ROLES_QUERY,
          variables: { accountId },
          fetchPolicy: 'network-only',
        });

        if (errors) {
          console.error('Error fetching team roles:', errors);
          return;
        }

        this.teamRoles = data.app?.teamRoles || [];
      } finally {
        this.loading = false;
      }
    },
    async fetchTeamRole(roleId: number) {
      this.loading = true;
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      try {
        const { data, errors } = await apiClient.query({
          query: GET_TEAM_ROLE_DETAILS_QUERY,
          variables: { roleId },
          fetchPolicy: 'network-only',
        });

        if (errors) {
          return;
        }

        this.teamRoleDetails = data.app?.teamRoleDetails || {};
      } finally {
        this.loading = false;
      }
    },

    async saveTeamRole(payload: TeamRoleInput): Promise<RequestResult<TeamRoleDetails.TeamRoleDetails>> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.loading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: SAVE_TEAM_ROLE_MUTATION,
          variables: { input: payload },
        });

        if (data?.app?.saveTeamRole?.errors) {
          return { errors: data?.app.saveTeamRole.errors };
        }

        if (data?.app?.saveTeamRole?.teamRoleDetails) {
          return { result: TeamRoleDetails.fromGql(data?.app?.saveTeamRole?.teamRoleDetails) };
        }
        return {};
      } finally {
        this.loading = false;
      }
    },

    async deleteTeamRole(teamRoleId: number) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this.loading = true;

      try {
        await apiClient.mutate<Mutation>({
          mutation: DELETE_TEAM_ROLE_MUTATION,
          variables: {
            teamRoleId,
          },
        });
      } finally {
        this.loading = false;
      }
    },
  },
});
